/**
 * Ajouter une exigence au dossier — chunk 9.AC.1.
 *
 * Pièces, notes, formulaires et mandats réutilisent les contrats existants.
 * Les formulaires et mandats désignent une version publiée et bloquent l'envoi.
 *
 * `company_field` et `contact_ref` existent dans le modèle et s'ajoutent par
 * l'API : les exposer ici supposerait deux sélecteurs de plus — colonnes
 * d'entreprise, référence de contact — pour un geste rare. Ils viendront quand
 * un usage réel les demandera.
 *
 * ## Le formulaire, lui, a trouvé son usage
 *
 * `form_response` est désormais proposé : c'est le besoin qui a motivé ce chunk
 * — exiger d'un dossier qu'un questionnaire soit rempli avant l'envoi aux
 * assureurs.
 *
 * **La clé désigne une VERSION, pas un modèle**, et c'est délibéré : une
 * exigence posée en mars reste satisfaite quand le cabinet publie une v2 en
 * août. Le même raisonnement vaut déjà pour `mandate`.
 *
 * **Rien n'est spécifique à un formulaire particulier.** Un modèle publié
 * devient exigible comme n'importe quel autre — aucun cas codé en dur.
 */

import { publicUnfillableRequiredFields } from "@kollectivia/shared";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFormTemplates, useFormTemplateVersions } from "../../../lib/queries/formTemplates";
import {
  useCreateManualMissing,
  useCreateProjectRequirement,
} from "../../../lib/queries/projectCompleteness";

type Props = {
  readonly projectId: string;
  readonly onClose: () => void;
};

type Nature = "document" | "manual" | "form_response" | "mandate";
type Audience = "client" | "broker" | "admin" | "insurer";

/** `Registre des employés` → `registre-des-employes`. */
function versCle(libelle: string): string {
  return libelle
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function AddRequirementDialog({ projectId, onClose }: Props) {
  const { t } = useTranslation();
  const idNature = useId();
  const idLibelle = useId();
  const idAudience = useId();
  const idLibelleEn = useId();
  const idEcheance = useId();

  const idModele = useId();
  const idVersion = useId();

  const [nature, setNature] = useState<Nature>("document");
  const [modeleId, setModeleId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [libelle, setLibelle] = useState("");
  const [libelleEn, setLibelleEn] = useState("");
  const versionRequise = nature === "form_response" || nature === "mandate";
  const [audience, setAudience] = useState<Audience>("client");
  const [echeance, setEcheance] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);

  // Un modèle sans version publiée n'est pas exigible : `requirement_key` doit
  // désigner une version publiée, et l'API refuserait. Ne pas le proposer évite
  // de faire découvrir ce refus après coup.
  const modeles = useFormTemplates({ status: "active" });
  const exigibles = (modeles.data?.items ?? []).filter(
    (m) => m.latest_published_version_id !== null,
  );
  const versions = useFormTemplateVersions(modeleId || undefined);
  /** Le nom du modèle choisi — sert à savoir si le libellé est encore automatique. */
  const libelleAuto = exigibles.find((m) => m.id === modeleId)?.name ?? "";
  const versionsPubliees = (versions.data?.items ?? []).filter((v) => v.published_at !== null);
  const versionChoisie = versionsPubliees.find((v) => v.id === versionId);
  const reserveAuCabinet =
    versionChoisie !== undefined &&
    publicUnfillableRequiredFields(versionChoisie.schema).length > 0;

  const creerExigence = useCreateProjectRequirement(projectId);
  const creerNote = useCreateManualMissing(projectId);
  const enCours = creerExigence.isPending || creerNote.isPending;

  const cle = versCle(libelle);
  // Un libellé qui ne produit aucune clé exploitable — « ??? », « --- » — donnerait
  // une exigence sans identité stable. Mieux vaut le dire tout de suite.
  const libelleUtilisable = libelle.trim().length > 0 && cle.length > 0;
  // Pour un formulaire, c'est la VERSION qui identifie l'exigence — le libellé
  // n'en est que l'étiquette lisible.
  const pretASoumettre = versionRequise
    ? versionChoisie !== undefined &&
      libelle.trim().length > 0 &&
      !(audience === "client" && reserveAuCabinet)
    : libelleUtilisable;

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    if (!pretASoumettre || enCours) return;
    setErreur(null);
    // Le libellé est saisi une fois et sert aux deux langues. Demander une
    // traduction pour une pièce interne ferait abandonner le conseiller au
    // deuxième champ ; l'interface bilingue reste celle des libellés du produit,
    // pas celle des notes qu'il prend sur un dossier.
    const commun = {
      project_id: projectId,
      label_fr: libelle.trim(),
      label_en: versionRequise && libelleEn.trim() ? libelleEn.trim() : libelle.trim(),
      audience,
      due_at: echeance ? new Date(`${echeance}T12:00:00Z`).toISOString() : null,
    };

    try {
      if (versionRequise) {
        // La clé EST la version. Le serveur revérifie qu'elle est publiée et du
        // bon cabinet — nommer ne suffit pas.
        // `blocks_insurer_send` vaut `false` par défaut en base. Sans ce
        // drapeau, l'exigence s'afficherait comme requise et la readiness
        // laisserait pourtant partir la demande — le mot « requis » ne
        // voudrait plus rien dire. Relevé par la revue Codex de la PR #146.
        await creerExigence.mutateAsync({
          ...commun,
          kind: nature as "form_response" | "mandate",
          requirement_key: versionId,
          blocks_insurer_send: true,
        });
      } else if (nature === "document") {
        await creerExigence.mutateAsync({ ...commun, kind: "document", requirement_key: cle });
      } else {
        await creerNote.mutateAsync(commun);
      }
      onClose();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : t("completeness.dialog.error"));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${idNature}-titre`}
    >
      <form
        onSubmit={soumettre}
        className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-lg"
      >
        <h2 id={`${idNature}-titre`} className="text-base font-semibold text-slate-900">
          {t("completeness.dialog.title")}
        </h2>

        <fieldset className="mt-4">
          <legend className="text-sm font-medium text-slate-700">
            {t("completeness.dialog.nature")}
          </legend>
          <div className="mt-2 flex flex-wrap gap-4">
            {(["document", "form_response", "mandate", "manual"] as const).map((n) => (
              <label key={n} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name={idNature}
                  value={n}
                  checked={nature === n}
                  onChange={() => setNature(n)}
                  className="focus-visible:ring-brand-500 focus-visible:outline-none focus-visible:ring-2"
                />
                {t(`completeness.kind.${n}`)}
              </label>
            ))}
          </div>
        </fieldset>

        {versionRequise &&
          (exigibles.length === 0 ? (
            <p className="mt-4 text-sm text-slate-600">{t("completeness.dialog.form_none")}</p>
          ) : (
            <>
              <label htmlFor={idModele} className="mt-4 block text-sm font-medium text-slate-700">
                {t("completeness.dialog.form_template")}
              </label>
              <select
                id={idModele}
                value={modeleId}
                onChange={(e) => {
                  const choisi = e.target.value;
                  setModeleId(choisi);
                  // La dernière version publiée est le défaut : c'est celle
                  // qu'un conseiller veut exiger neuf fois sur dix.
                  const m = exigibles.find((x) => x.id === choisi);
                  setVersionId(m?.latest_published_version_id ?? "");
                  // Le libellé suit le modèle tant que le conseiller ne l'a pas
                  // écrit lui-même — sinon il devrait le retaper à chaque essai.
                  if (libelle.trim().length === 0 || libelle === libelleAuto) {
                    setLibelle(m?.name ?? "");
                  }
                }}
                className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
              >
                <option value="">{t("completeness.dialog.form_template")}…</option>
                {exigibles.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>

              {modeleId !== "" && (
                <>
                  <label
                    htmlFor={idVersion}
                    className="mt-4 block text-sm font-medium text-slate-700"
                  >
                    {t("completeness.dialog.form_version")}
                  </label>
                  <select
                    id={idVersion}
                    value={versionId}
                    onChange={(e) => setVersionId(e.target.value)}
                    className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
                  >
                    {versionsPubliees.map((v) => (
                      <option key={v.id} value={v.id}>
                        {t("completeness.dialog.form_version_label", { n: v.version_number })}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-600">
                    {t("completeness.dialog.form_hint")}
                  </p>
                  {reserveAuCabinet && (
                    <p className="mt-2 text-sm text-amber-800" role="status">
                      {t("completeness.dialog.form_broker_only")}
                    </p>
                  )}
                </>
              )}
            </>
          ))}

        <label htmlFor={idLibelle} className="mt-4 block text-sm font-medium text-slate-700">
          {t("completeness.dialog.label")}
        </label>
        <input
          id={idLibelle}
          type="text"
          value={libelle}
          onChange={(e) => setLibelle(e.target.value)}
          maxLength={200}
          required
          autoComplete="off"
          placeholder={t("completeness.dialog.label_placeholder")}
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
        />

        {versionRequise && (
          <>
            <label htmlFor={idLibelleEn} className="mt-4 block text-sm font-medium text-slate-700">
              {t("completeness.dialog.label_en_optional")}
            </label>
            <input
              id={idLibelleEn}
              type="text"
              value={libelleEn}
              onChange={(e) => setLibelleEn(e.target.value)}
              maxLength={200}
              autoComplete="off"
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </>
        )}

        <label htmlFor={idAudience} className="mt-4 block text-sm font-medium text-slate-700">
          {t("completeness.dialog.audience")}
        </label>
        <select
          id={idAudience}
          value={audience}
          onChange={(e) => setAudience(e.target.value as Audience)}
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
        >
          {(["client", "broker", "admin", "insurer"] as const).map((a) => (
            <option key={a} value={a}>
              {t(`completeness.waiting_on.${a}`)}
            </option>
          ))}
        </select>

        <label htmlFor={idEcheance} className="mt-4 block text-sm font-medium text-slate-700">
          {t("completeness.dialog.due")}
        </label>
        <input
          id={idEcheance}
          type="date"
          value={echeance}
          onChange={(e) => setEcheance(e.target.value)}
          className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
        />

        {erreur !== null && (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {erreur}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="focus-visible:ring-brand-500 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={enCours || !pretASoumettre}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 rounded-lg px-3 py-1.5 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {enCours ? t("completeness.dialog.saving") : t("completeness.dialog.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
